/**
 * EditMessageModal Component
 *
 * Modal for editing a message with edit history preview.
 */
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';

export interface EditHistoryEntry {
  content: string;
  edited_at: string;
}

export interface Message {
  id: string;
  content: string;
  is_edited?: boolean;
  edited_at?: string;
  edit_history?: EditHistoryEntry[];
}

interface EditMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: Message | null;
  onSave: (messageId: string, newContent: string) => Promise<void>;
}

export const EditMessageModal: React.FC<EditMessageModalProps> = ({
  isOpen,
  onClose,
  message,
  onSave,
}) => {
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (message) {
      setContent(message.content);
      setError(null);
    }
  }, [message]);

  const handleSave = async () => {
    if (!message || !content.trim()) return;

    if (content.trim() === message.content.trim()) {
      onClose();
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(message.id, content.trim());
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  if (!message) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Message"
      size="lg"
    >
      <div className="space-y-4">
        {/* Edit History Toggle */}
        {message.edit_history && message.edit_history.length > 0 && (
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            <svg className={`w-4 h-4 transform transition-transform ${showHistory ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            View edit history ({message.edit_history.length} edits)
          </button>
        )}

        {/* Edit History */}
        {showHistory && message.edit_history && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 max-h-48 overflow-y-auto">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Edit History
            </h4>
            <div className="space-y-3">
              {message.edit_history.map((entry, index) => (
                <div key={index} className="text-sm border-b border-gray-200 dark:border-gray-700 pb-2 last:border-0">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {formatDate(entry.edited_at)}
                  </div>
                  <div className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                    {entry.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Editor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Message Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full min-h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg
              bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
              focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
            placeholder="Enter your message..."
            disabled={isSaving}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-500/10 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || !content.trim()}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditMessageModal;
